import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Tag, Calendar, Sparkles, ShieldCheck, PenLine } from "lucide-react";
import DOMPurify from "dompurify";
import { API_BASE } from "@/lib/api";
import { APP_VERSION } from "@/version";
import StatusPill from "@/components/StatusPill";

/**
 * Parse a Keep-a-Changelog markdown body into an array of releases:
 *   [{ version, date, sections: { Added: [...], Changed: [...] } }]
 */
function parseChangelog(md) {
  const releases = [];
  const lines = md.split(/\r?\n/);
  let current = null, sectionKey = null;
  for (const raw of lines) {
    const line = raw.trimEnd();
    // release header:  ## [1.4.0] — 2026-02-27  (em- or hyphen-dash, any spaces)
    const rm = line.match(/^##\s+\[?([^\]\s]+)\]?\s*[—\-–]\s*(.+)$/);
    if (rm) {
      current = { version: rm[1], date: rm[2].trim(), sections: {} };
      releases.push(current);
      sectionKey = null;
      continue;
    }
    if (!current) continue;
    const sm = line.match(/^###\s+(.+)$/);
    if (sm) { sectionKey = sm[1].trim(); current.sections[sectionKey] = []; continue; }
    if (sectionKey && line.startsWith("- ")) {
      current.sections[sectionKey].push(line.slice(2).trim());
    }
  }
  return releases;
}

/** Render inline markdown emphasis: **bold**, `code`, [text](url). Safe (escapes HTML first, then sanitises with DOMPurify). */
function renderInline(text) {
  const escape = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  let html = escape(text);
  html = html.replace(/`([^`]+)`/g, '<code class="bg-slate-100 border border-slate-200 rounded px-1 py-0.5 font-mono text-[11.5px] text-slate-800">$1</code>');
  html = html.replace(/\*\*([^*]+)\*\*/g, '<strong class="font-semibold text-slate-900">$1</strong>');
  html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a class="text-cyan-700 hover:text-cyan-900 underline" href="$2" target="_blank" rel="noopener noreferrer">$1</a>');
  const clean = DOMPurify.sanitize(html, { ALLOWED_TAGS: ["code", "strong", "a"], ALLOWED_ATTR: ["class", "href", "target", "rel"] });
  return { __html: clean };
}

const SECTION_STYLE = {
  Added:      { icon: Sparkles,     tone: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  Changed:    { icon: PenLine,      tone: "text-cyan-700 bg-cyan-50 border-cyan-200" },
  Fixed:      { icon: ShieldCheck,  tone: "text-amber-700 bg-amber-50 border-amber-200" },
  Removed:    { icon: PenLine,      tone: "text-rose-700 bg-rose-50 border-rose-200" },
  Security:   { icon: ShieldCheck,  tone: "text-rose-700 bg-rose-50 border-rose-200" },
  Deprecated: { icon: PenLine,      tone: "text-slate-700 bg-slate-50 border-slate-200" },
};

export default function Changelog() {
  const [releases, setReleases] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API_BASE}/changelog`, { credentials: "omit" });
        if (!r.ok) throw new Error(String(r.status));
        const md = await r.text();
        if (cancelled) return;
        const parsed = parseChangelog(md);
        setReleases(parsed);
      } catch (e) { if (!cancelled) setError(e.message); }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="min-h-screen bg-[#FBFBF9] text-slate-900">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-white/85 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-4xl mx-auto flex items-center justify-between px-6 py-4">
          <Link to="/" className="flex items-center gap-2.5" data-testid="changelog-brand">
            <div className="h-8 w-8 rounded-md bg-gradient-to-br from-rose-500 via-amber-500 to-emerald-500 p-[1.5px]">
              <div className="h-full w-full rounded-[5px] bg-[#050B1F] flex items-center justify-center">
                <div className="h-1.5 w-1.5 rounded-full bg-rose-400" />
              </div>
            </div>
            <div className="font-display font-bold text-[18px] tracking-tight">RiskEye</div>
            <div className="text-[10px] font-mono uppercase tracking-[0.24em] text-slate-400 ml-1 pb-[1px]">Changelog</div>
          </Link>
          <div className="flex items-center gap-3">
            <StatusPill size="sm" />
            <Link to="/" className="text-[13px] text-slate-600 hover:text-slate-900 flex items-center gap-1.5" data-testid="changelog-home">
              <ArrowLeft className="h-3.5 w-3.5" /> Home
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-4xl mx-auto px-6 pt-14 pb-8">
        <div className="text-[10px] uppercase tracking-[0.32em] font-mono text-slate-400 mb-4">Release notes</div>
        <h1 className="font-display font-bold text-[42px] leading-[1.05] tracking-tight max-w-3xl">
          What's new in <span className="text-cyan-700">RiskEye</span>.
        </h1>
        <p className="mt-5 text-[15px] text-slate-600 leading-relaxed max-w-2xl">
          Every production release, in reverse chronological order. Follow <a href="https://keepachangelog.com/" target="_blank" rel="noopener noreferrer" className="text-cyan-700 hover:text-cyan-900 underline">Keep a Changelog</a> conventions; the current build is <span className="font-mono font-semibold text-slate-800">v{APP_VERSION}</span>.
        </p>
      </section>

      {/* Timeline */}
      <section className="max-w-4xl mx-auto px-6 pb-16">
        {error && (
          <div className="rounded-lg border border-rose-200 bg-rose-50 text-rose-800 px-4 py-3 text-sm" data-testid="changelog-error">
            Could not load changelog ({error}). Please try again shortly.
          </div>
        )}
        {!releases && !error && (
          <div className="text-sm text-slate-500" data-testid="changelog-loading">Loading release notes…</div>
        )}
        {releases && releases.length === 0 && (
          <div className="text-sm text-slate-500">No releases published yet.</div>
        )}
        <div className="relative">
          {releases && releases.length > 0 && <div className="absolute left-[7px] top-3 bottom-3 w-px bg-slate-200" aria-hidden />}
          <div className="space-y-8">
            {releases?.map((r, i) => (
              <article key={r.version} className="relative pl-8" data-testid={`release-${r.version}`}>
                {/* Timeline dot */}
                <span
                  className={`absolute left-0 top-2 h-3.5 w-3.5 rounded-full border-2 ${i === 0 ? "bg-cyan-500 border-cyan-200 shadow-[0_0_0_4px_rgba(6,182,212,0.15)]" : "bg-white border-slate-300"}`}
                  aria-hidden
                />
                <div className="flex flex-wrap items-baseline gap-3 mb-2">
                  <div className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2 py-1 text-xs font-mono font-semibold text-slate-800">
                    <Tag className="h-3 w-3 text-slate-400" /> v{r.version}
                  </div>
                  {i === 0 && (
                    <span className="inline-flex items-center gap-1 rounded-full border border-cyan-200 bg-cyan-50 text-cyan-800 px-2 py-0.5 text-[10.5px] font-mono uppercase tracking-widest">
                      Current
                    </span>
                  )}
                  <div className="inline-flex items-center gap-1 text-xs text-slate-500 font-mono">
                    <Calendar className="h-3 w-3" /> {r.date}
                  </div>
                </div>
                <div className="rounded-xl border border-slate-200 bg-white p-5">
                  {Object.entries(r.sections).map(([sk, items]) => {
                    const style = SECTION_STYLE[sk] || { icon: PenLine, tone: "text-slate-700 bg-slate-50 border-slate-200" };
                    const Icon = style.icon;
                    return (
                      <div key={sk} className="mb-5 last:mb-0">
                        <div className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[10.5px] font-mono uppercase tracking-widest ${style.tone} mb-2.5`}>
                          <Icon className="h-3 w-3" /> {sk}
                        </div>
                        <ul className="space-y-2 text-sm text-slate-700 leading-relaxed">
                          {items.map((it, j) => (
                            <li key={`${sk}-${j}-${it.slice(0, 24)}`} className="flex gap-2">
                              <span className="text-slate-400 shrink-0 pt-1">▸</span>
                              {/* XSS-safe: renderInline() sanitises via DOMPurify with a strict allowlist (code/strong/a + class/href/target/rel). */}
                              <span dangerouslySetInnerHTML={renderInline(it)} />
                            </li>
                          ))}
                        </ul>
                      </div>
                    );
                  })}
                  {Object.keys(r.sections).length === 0 && <div className="text-sm text-slate-400 italic">No details.</div>}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-200 bg-white/60">
        <div className="max-w-4xl mx-auto px-6 py-6 flex items-center justify-between text-[11.5px] font-mono uppercase tracking-[0.22em] text-slate-400">
          <span>© {new Date().getFullYear()} RiskEye · a seQure Product</span>
          <Link to="/trust" className="hover:text-slate-700">Trust & Security →</Link>
        </div>
      </footer>
    </div>
  );
}
